import React, { type ReactNode } from "react";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import U2000Panel from "../../layout/U2000Panel";
import U2000InvalidFeedback from "../feedback/U2000InvalidFeedback";

interface Option {
  value: string;
  label: string;
}

interface U2000RadioGroupStringProps {
  titleGroup?: string;
  orientation?: "horizontal" | "vertical";
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  options: Option[];
  readonly?: boolean;
  required?: boolean;
  /** Nom HTML du groupe (`name` des inputs). Défaut : `fieldName`. À différencier si plusieurs formulaires coexistent. */
  radioGroupName?: string;
  /** Contenu affiché dans le même panneau que les radios (ex. champ lié au groupe). */
  children?: ReactNode;
}

/**
 * U2000RadioGroupString est une version de U2000RadioGroup qui gère la valeur sélectionnée comme une string simple,
 * et le formStateDispatch reçoit une string (et non un array de string).
 */
const U2000RadioGroupString: React.FC<U2000RadioGroupStringProps> = ({
  titleGroup,
  orientation = "horizontal",
  formStateHandlers,
  fieldName,
  options,
  readonly = false,
  required = false,
  radioGroupName,
  children,
}) => {
  // Ajoute une fonction utilitaire pour accéder à une propriété imbriquée
  function getNestedValue(obj: Record<string, any>, path: string): any {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  }

  function setNestedValue(obj: Record<string, any>, path: string, value: any): Record<string, any> {
    const keys = path.split(".");
    const lastKey = keys.pop()!;
    const deep = keys.reduce((acc, key) => {
      if (!acc[key]) acc[key] = {};
      return acc[key];
    }, obj);
    deep[lastKey] = value;
    return obj;
  }

  // Gestion du changement façon U2000InputText si formStateDispatch fourni
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (readonly) return; // Ne rien faire si readonly
    let newValue: string;
    try {
      const parsed = JSON.parse(e.target.value);
      // Stocker comme string (pas array)
      newValue = parsed && typeof parsed === "object" && "value" in parsed ? parsed.value : parsed;
    } catch {
      newValue = e.target.value;
    }
    // Utiliser onChange pour déclencher la validation automatiquement
    if (formStateHandlers.onChange) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          id: fieldName,
          value: String(newValue),
        },
      } as React.ChangeEvent<HTMLInputElement>;
      formStateHandlers.onChange(syntheticEvent, newValue);
    } else {
      // Fallback si onChange n'est pas disponible (ne devrait pas arriver normalement)
      const currentValue = formStateHandlers.getData();
      setNestedValue(currentValue, fieldName, newValue);
      formStateHandlers.setData({ ...currentValue });
    }
  }

  // Gestion du blur pour marquer le champ comme "touched" et déclencher la validation
  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    if (formStateHandlers.onBlur) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          id: fieldName,
        },
      } as React.FocusEvent<HTMLInputElement>;
      formStateHandlers.onBlur(syntheticEvent);
    }
  }

  // Applique le style disabled/readonly uniquement sur l'input radio, pas sur le label
  const value = getNestedValue(formStateHandlers.getData(), fieldName);
  const selectedValue = typeof value === "string" ? value : Array.isArray(value) && value.length > 0 ? value[0].toString() : "";
  const disabled = readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;

  // Vérifier s'il y a une erreur pour ce champ
  const errors = formStateHandlers.getErrors();
  const touched = formStateHandlers.getTouched();
  const isSubmitted = formStateHandlers.isSubmitted();
  const hasError = (touched[fieldName] || isSubmitted) && errors[fieldName];

  const radioClass = `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-200 select-none ${disabled ? "cursor-not-allowed" : "cursor-pointer hover:bg-muted"}`;

  // Classes modernes pour les radio buttons avec le design système
  // Style normal
  const radioInputClassEnabled = "h-4 w-4 text-blue-600 bg-white border-gray-300 focus:ring-blue-500 focus:ring-2 transition-all duration-200";
  // Style avec erreur
  const radioInputClassError = "h-4 w-4 text-red-600 bg-white border-red-300 focus:ring-red-500 focus:ring-2 transition-all duration-200";
  // Style disabled
  const radioInputClassDisabled = "h-4 w-4 text-gray-500 bg-muted border surface-border cursor-not-allowed";

  // Classes pour les libellés
  const labelSpanClassNormal = disabled ? "text-sm text-gray-500 leading-relaxed" : "text-sm text-gray-900 leading-relaxed";
  const labelSpanClassError = disabled ? "text-sm text-gray-500 leading-relaxed" : "text-sm text-red-600 leading-relaxed font-medium";
  const labelSpanClass = hasError ? labelSpanClassError : labelSpanClassNormal;

  // Déterminer la classe du bouton radio selon l'état
  const getRadioInputClass = () => {
    if (disabled) return radioInputClassDisabled;
    if (hasError) return radioInputClassError;
    return radioInputClassEnabled;
  };

  // Construire le titre avec l'astérisque si required
  const panelTitle: React.ReactNode | undefined = titleGroup ? (
    <>
      {titleGroup}
      {required && <span className="text-red-500 ml-1" aria-label="requis">*</span>}
    </>
  ) : undefined;

  const inputGroupName = radioGroupName ?? fieldName;

  const content = (
    <div className="space-y-1.5">
      <div role="radiogroup" aria-label={titleGroup ?? `${fieldName} options`} className={`flex ${titleGroup ? "" : "pt-3"} ${orientation === "vertical" ? "flex-col gap-y-1.5" : "flex-row gap-x-6 items-center"}`}>
        {options.map((option, index) => (
          <label key={option.value} className={radioClass}>
            <input
              id={`${fieldName}-${option.value}`}
              type="radio"
              name={inputGroupName}
              value={JSON.stringify({ value: option.value })}
              checked={selectedValue === option.value}
              onChange={readonly ? undefined : handleChange}
              onBlur={readonly ? undefined : handleBlur}
              readOnly={readonly}
              disabled={disabled}
              required={required && index === 0}
              aria-checked={selectedValue === option.value}
              aria-invalid={!!hasError}
              aria-required={required}
              aria-describedby={hasError ? `${fieldName}-error` : undefined}
              className={getRadioInputClass()}
            />
            <span className={labelSpanClass}>{option.label}</span>
          </label>
        ))}
      </div>
      {/* Affichage du message d'erreur */}
      <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName={fieldName} />
    </div>
  );
  // Si titleGroup est fourni, on utilise U2000Panel avec le titre incluant l'astérisque
  const appended = children ? <div className="mt-3 w-full">{children}</div> : null;

  return titleGroup ? (
    <U2000Panel title={panelTitle} titleAlign="left" className="flex h-full w-full flex-col items-stretch justify-start gap-0">
      {content}
      {appended}
    </U2000Panel>
  ) : (
    <>
      {content}
      {appended}
    </>
  );
};

export default U2000RadioGroupString;
